import {
  ReactNode,
  useContext,
  useEffect,
  createRef,
  useState,
  useCallback,
  useMemo,
} from 'react';

import { useRouter } from 'next/router';

import { REGEX } from '@lessonPlanner/regex';

import { ThreadContextType, ThreadPageData } from '../../interfaces/thread';
import { createPageComponent } from '../../utils/createPageComponent';

import ThreadContext from './context';

interface ThreadProviderProps {
  children: ReactNode;
}

export function ThreadProvider({ children }: ThreadProviderProps) {
  const [pages, setPages] = useState<ThreadPageData[]>([]);
  const [isThreadedMode, setThreadedMode] = useState(false);
  const router = useRouter();

  const clearAllPages = useCallback(() => {
    setPages([]);
  }, []);

  const addPage = useCallback((path: string, component: ReactNode): string => {
    const id = `${path.replace(
      REGEX.CLEAN_THREAD_PATH,
      '-',
    )}-${crypto.randomUUID()}`;
    const resultsRef = createRef<HTMLDivElement>();

    const newPage: ThreadPageData = {
      id,
      path,
      component,
      resultsRef,
      isGenerating: false,
    };

    setPages((prev) => [...prev, newPage]);
    return id;
  }, []);

  const removePage = useCallback((id: string) => {
    setPages((prev) => prev.filter((page) => page.id !== id));
  }, []);

  useEffect(() => {
    return () => {
      clearAllPages();
    };
  }, [clearAllPages]);

  useEffect(() => {
    const threaded = router.query.threaded;
    const addCurrentPageToThread = () => {
      const currentPath = router.asPath.split('?')[0];
      addPage(currentPath, createPageComponent(currentPath));
    };

    if (threaded && !isThreadedMode) {
      setThreadedMode(true);
      addCurrentPageToThread();
    } else if (!threaded && isThreadedMode) {
      // Clean up when exiting threaded mode
      setThreadedMode(false);
      clearAllPages();
    }
  }, [
    router.query.threaded,
    isThreadedMode,
    router.asPath,
    addPage,
    clearAllPages,
  ]);

  const getResultsRef = useCallback(
    (pageId: string) => {
      const page = pages.find((p) => p.id === pageId);
      return page?.resultsRef || null;
    },
    [pages],
  );

  const setPageGenerating = useCallback(
    (pageId: string, isGenerating: boolean) => {
      setPages((prev) =>
        prev.map((page) =>
          page.id === pageId ? { ...page, isGenerating } : page,
        ),
      );
    },
    [],
  );
  const contextValue: ThreadContextType = useMemo(
    () => ({
      pages,
      addPage,
      removePage,
      getResultsRef,
      setPageGenerating,
      isThreadedMode,
      setThreadedMode,
      createPageComponent,
      clearAllPages,
    }),
    [
      pages,
      addPage,
      removePage,
      getResultsRef,
      setPageGenerating,
      isThreadedMode,
      setThreadedMode,
      clearAllPages,
    ],
  );

  return (
    <ThreadContext.Provider value={contextValue}>
      {children}
    </ThreadContext.Provider>
  );
}

export const useThreadContext = () => {
  const context = useContext(ThreadContext);
  if (!context) {
    throw new Error('useThreadContext must be used within ThreadProvider');
  }
  return context;
};
